import { getMonthlySentimentState } from '@terminal/views/theme-overview/tab-content/pulse-overview/hooks/usePulseSummaries';
import { useSentimentScope } from '@terminal/views/theme-overview/tab-content/pulse-overview/hooks/useSentimentScope';
import {
  PulseMonthlySummary,
  PulseSubject,
} from '@terminal/views/theme-overview/tab-content/pulse-overview/pulse-types';
import { useEffect } from 'react';
import { usePulseSentimentInsights } from './usePulseSentimentInsights';

const DIRECTIONS = ['tailwind', 'headwind'] as const;
type CardDirection = (typeof DIRECTIONS)[number];

export const useMonthlySentimentState = ({
  summary,
  subject,
  direction,
}: {
  summary?: PulseMonthlySummary;
  subject: PulseSubject;
  direction: CardDirection;
}) => {
  const theme = subject.type === 'theme' ? subject.item : undefined;
  const sentimentScope = useSentimentScope(theme);
  const needsAllDrivers = !summary || !summary.narrative;
  const canLoadAllDrivers =
    needsAllDrivers && (!theme || sentimentScope.isReady);
  const allDrivers = usePulseSentimentInsights({
    subject,
    themeIds: theme ? sentimentScope.themeIds : undefined,
    companyIds: theme ? sentimentScope.companyIds : undefined,
    direction,
    enabled: canLoadAllDrivers,
  });
  const shouldFetchNextPage =
    canLoadAllDrivers &&
    !allDrivers.isLoading &&
    allDrivers.data.length === 0 &&
    allDrivers.hasNextPage &&
    !allDrivers.isFetchingNextPage &&
    !allDrivers.isError;

  useEffect(() => {
    if (shouldFetchNextPage) {
      void allDrivers.fetchNextPage();
    }
  }, [allDrivers.fetchNextPage, shouldFetchNextPage]);

  const isLoadingAllDrivers =
    sentimentScope.isLoading ||
    allDrivers.isLoading ||
    allDrivers.isFetchingNextPage ||
    shouldFetchNextPage;
  const hasAllDrivers =
    allDrivers.data.length > 0 || allDrivers.isError || sentimentScope.isError;

  return getMonthlySentimentState(summary, hasAllDrivers, isLoadingAllDrivers);
};
